/**
 * The candidate slate — every NEW collection the corpus can actually fill, enumerated and counted
 * BEFORE any model is asked anything.
 *
 * Evan (2026-08-23, approving the assistant): it must "return a proposed SQL predicate + title +
 * a LIVE row count", and — his warning, the one that shapes this whole file — "an assistant that
 * proposes collections returning 3 venues is worse than no assistant."
 *
 * ─── WHY ENUMERATION AND NOT GENERATION ──────────────────────────────────────────────────────
 * A collection is NOT an arbitrary SQL predicate. `search_guides.kind` is a CHECK constraint over
 * sixteen values and the membership logic for each lives in `search_guide_venue_ids`
 * (20260870000000, ~250 lines). A model cannot write a new predicate; the only thing it can
 * actually choose is a KIND plus its one PARAMETER — `tag`, `categories`, the criterion name — and
 * the copy that goes on the rail.
 *
 * Of the sixteen kinds, fourteen are singletons: there is exactly one "Trending worldwide", one
 * "Underground", one "{city} classics", and all of them already exist as rows. `criterion` has
 * five possible values and four are already live. That leaves `tag` (109 distinct values in
 * production) and `category` (~50) as the entire open-ended proposal space — and both are small
 * enough to enumerate EXHAUSTIVELY in the browser.
 *
 * So we enumerate first, count first, and hand the model a pre-counted shortlist. It ranks and
 * names; it cannot invent a spec, and therefore it CANNOT propose an empty collection. That is a
 * structural guarantee, not a prompt instruction — the same shape as `search-concierge`, where
 * Postgres does retrieval and the model only writes the sentence.
 *
 * ─── THE PREDICATE REPLICATED HERE, AND THE PROOF THAT IT MATCHES ────────────────────────────
 * `countsFor` reproduces exactly three things from `search_guide_venue_ids`: the eligible-venue
 * pool, the 2-distinct-rater floor, and the `tag` / `category` membership arms. Nothing else —
 * ordering, covers, the overlap cap and the other fourteen arms are never touched, because a
 * COUNT does not need them.
 *
 * ⚠️ VERIFIED AGAINST PRODUCTION, 2026-08-24, New Orleans. This enumeration said the only tags
 * clearing four venues are indoor 7 / chill 4 / outdoor 4. Production's own
 * `search_guide_venue_ids(guide_id, <New Orleans>, 1000)` on the five tag guides that exist:
 *     chill 4 ✓   outdoor 4 ✓   beer 3 (excluded ✓)   dancey 2 (excluded ✓)   dark 1 (excluded ✓)
 * Five for five. If that ever stops matching, the floor or the pool moved in 870 and this file is
 * the thing that is wrong.
 *
 * ⚠️ IT IS STILL A REPLICA, SO IT IS AN ESTIMATE AND IS LABELLED ONE. The authoritative number is
 * `admin_list_search_guides(p_city_id).venue_count`, which calls the real function — and the
 * assistant panel fetches exactly that the moment a proposal is materialised as a draft. This
 * file exists to make the SHORTLIST honest, not to replace the audit.
 *
 * ─── WHAT THE NUMBERS LOOK LIKE, SO NOBODY IS SURPRISED (production, 2026-08-24) ─────────────
 * 742 rated venues corpus-wide; 34 of them are photo-bearing and rated in New Orleans, and 21 of
 * those clear the 2-rater floor. Against that, the number of NEW tag collections New Orleans can
 * support is THREE, and the number of new category collections is ONE (Night Club, at exactly 4).
 * That is not a bug in the assistant. It is the answer, and the page says it in those words.
 */
import { supabase } from './supabase';
import { fetchAllRows } from './fetchAll';

/** Mirrors `search_guide_venue_ids`' 2-distinct-rater membership floor (20260870000000 §2). */
export const RATER_FLOOR = 2;

/** Mirrors `get_search_guides`' `p_min_venues` default — under this the app drops the rail. */
export const MIN_VENUES = 4;

/** The five criteria `kind='criterion'` accepts, exactly as `admin_upsert_search_guide` spells them. */
export const CRITERIA = ['drinks', 'atmosphere', 'music', 'service', 'lineup'] as const;

/**
 * One proposable collection, already counted.
 *
 * `id` is a stable synthetic key (`tag:dancey`, `category:Dive Bar`). It is the ONLY thing the
 * model may return to name a candidate — see `CollectionAssistantPanel`. A key that does not
 * resolve back to this slate is dropped, which is what makes an invented proposal impossible.
 */
export type Candidate = {
  id: string;
  kind: 'tag' | 'category' | 'criterion';
  /** Goes to `admin_upsert_search_guide` as `p_tag` (tag + criterion kinds). */
  tag: string | null;
  /** Goes to `admin_upsert_search_guide` as `p_categories`. */
  categories: string[] | null;
  /** Human-readable form of the membership rule, for the model and for the card. */
  predicate: string;
  /** Venues in the SELECTED city that satisfy the rule at the rater floor. */
  cityCount: number;
  /** Cities (of those measured) where the rule clears `MIN_VENUES`. */
  citiesClearing: number;
  /** Per-city counts, best first — the honest picture of a city-scoped rail's reach. */
  byCity: { city: string; n: number }[];
  /** True when a `search_guides` row already uses this exact kind+parameter. */
  alreadyExists: boolean;
  /** Set when `alreadyExists` — so the panel can say "exists but is switched off". */
  existingTitle: string | null;
  existingActive: boolean;
};

export type SlateCity = { id: string; name: string; pool: number };

export type Slate = {
  city: SlateCity | null;
  /** Photo-bearing rated venues in the selected city. */
  cityPool: number;
  /** …of those, the ones clearing the rater floor. This is the real denominator. */
  cityPoolFloored: number;
  candidates: Candidate[];
  /** Every tag/category considered, including the ones that fill nothing — the "why so few" list. */
  rejected: { id: string; label: string; cityCount: number }[];
  /**
   * EVERY row in `search_guides`, live or not, with whether it fills in the selected city.
   * The assistant needs the whole list to avoid proposing a rail that already exists: only two of
   * the sixteen kinds are parameterised, so the other fourteen rails never appear as candidates
   * and would otherwise be invisible to it.
   */
  liveRails: { title: string; kind: string; isActive: boolean; renders: boolean; venueCount: number }[];
  measuredCities: string[];
};

type VenueRow = {
  id: string;
  city_id: string | null;
  fsq_category: string | null;
  venue_shado_unique_users: number | null;
};

type LogRow = { venue_id: string | null; criteria_thought_tags: unknown };

type GuideRow = { kind: string; tag: string | null; categories: string[] | null; title: string; is_active: boolean };

/** Cities measured for the "reach" column. More than this and the read stops being one page. */
const REACH_CITIES = 10;

/**
 * The eligible pool, spelled the same way `admin_guide_preview_cities` and the `pool` CTE in
 * `search_guide_venue_ids` spell it: not hidden, has a usable photo, has at least one rater.
 * A venue with no photo cannot appear on a rail, so counting it would inflate every number.
 */
async function readPool(): Promise<VenueRow[]> {
  return fetchAllRows<VenueRow>((from, to) =>
    supabase
      .from('venues')
      .select('id, city_id, fsq_category, venue_shado_unique_users, approved_venue_photo_url, google_photo_r2_url, is_hidden')
      .gt('venue_shado_unique_users', 0)
      .not('city_id', 'is', null)
      .order('id')
      .range(from, to),
  ).then((rows) =>
    (rows as (VenueRow & { approved_venue_photo_url?: string | null; google_photo_r2_url?: string | null; is_hidden?: boolean })[])
      .filter((v) => v.is_hidden !== true && Boolean(v.approved_venue_photo_url || v.google_photo_r2_url))
      .map((v) => ({
        id: v.id,
        city_id: v.city_id,
        fsq_category: v.fsq_category,
        venue_shado_unique_users: v.venue_shado_unique_users,
      })),
  );
}

/**
 * Live logs only. `retired_at`/`deleted_at` are the retire-and-create flow's tombstones — a
 * retired log's tags are a rating that was replaced, and counting them would resurrect it.
 */
async function readTagLogs(): Promise<LogRow[]> {
  return fetchAllRows<LogRow>((from, to) =>
    supabase
      .from('logs')
      .select('venue_id, criteria_thought_tags')
      .is('retired_at', null)
      .is('deleted_at', null)
      .not('criteria_thought_tags', 'is', null)
      .order('venue_id')
      .range(from, to),
  );
}

/** `criteria_thought_tags` is `{criterion: [tag, …]}`; the membership arm lowercases both sides. */
function tagsOf(raw: unknown): string[] {
  if (!raw || typeof raw !== 'object') return [];
  const out: string[] = [];
  for (const list of Object.values(raw as Record<string, unknown>)) {
    if (!Array.isArray(list)) continue;
    for (const t of list) {
      const tag = String(t ?? '').trim().toLowerCase();
      if (tag) out.push(tag);
    }
  }
  return out;
}

function keyOf(kind: Candidate['kind'], param: string): string {
  return `${kind}:${param}`;
}

/** A `search_guides` row's identity for dedupe purposes — same shape as `keyOf`. */
function guideKey(g: GuideRow): string | null {
  if (g.kind === 'tag' && g.tag) return keyOf('tag', g.tag.toLowerCase());
  if (g.kind === 'criterion' && g.tag) return keyOf('criterion', g.tag.toLowerCase());
  if (g.kind === 'category' && g.categories?.length === 1) return keyOf('category', g.categories[0]);
  return null;
}

/**
 * Build the slate for one city.
 *
 * Three reads, all of them ones the dashboard already makes elsewhere (`venues` in
 * `CityOverviews`, `logs` + `criteria_thought_tags` in `StudioDataBridge`, `cities` in
 * `guidePreviewCities`). Nothing here writes, and nothing here needs the service role.
 */
export async function buildSlate(city: SlateCity | null, cities: SlateCity[]): Promise<Slate> {
  const [pool, logs, guidesRes] = await Promise.all([
    readPool(),
    readTagLogs(),
    supabase.rpc('admin_list_search_guides', { p_city_id: city?.id ?? null }),
  ]);

  /* A missing/failing guide list costs the dedupe flag, not the slate. */
  const existing = new Map<string, GuideRow>();
  const liveRails: Slate['liveRails'] = [];
  if (!guidesRes.error && Array.isArray(guidesRes.data)) {
    for (const raw of guidesRes.data as (Partial<GuideRow> & { renders?: boolean; venue_count?: number })[]) {
      liveRails.push({
        title: String(raw.title ?? ''),
        kind: String(raw.kind ?? ''),
        isActive: Boolean(raw.is_active),
        /* `renders` arrives only from the post-871 signature; fall back to the count + minimum. */
        renders: typeof raw.renders === 'boolean' ? raw.renders : Number(raw.venue_count ?? 0) >= MIN_VENUES,
        venueCount: Number(raw.venue_count ?? 0) || 0,
      });
      const g: GuideRow = {
        kind: String(raw.kind ?? ''),
        tag: raw.tag ?? null,
        categories: Array.isArray(raw.categories) ? raw.categories : null,
        title: String(raw.title ?? ''),
        is_active: Boolean(raw.is_active),
      };
      const k = guideKey(g);
      if (k && !existing.has(k)) existing.set(k, g);
    }
  }

  const floored = pool.filter((v) => (v.venue_shado_unique_users ?? 0) >= RATER_FLOOR);
  const venueCity = new Map<string, string>();
  floored.forEach((v) => {
    if (v.city_id) venueCity.set(v.id, v.city_id);
  });

  /* tag -> city -> distinct venues. Distinct: two logs at one venue are one venue on the rail. */
  const tagCityVenues = new Map<string, Map<string, Set<string>>>();
  for (const l of logs) {
    const vid = l.venue_id;
    if (!vid) continue;
    const cid = venueCity.get(vid);
    if (!cid) continue; /* below the floor, unphotographed, or hidden — not on any rail */
    /* No per-log dedupe of the tag list: the accumulator is a Set of VENUE ids, so a log carrying
       "chill" under two criteria adds the same venue twice and the Set absorbs it. (A literal
       `for … of new Set(…)` would also need downlevelIteration — this tsconfig targets es5.) */
    for (const tag of tagsOf(l.criteria_thought_tags)) {
      let byCity = tagCityVenues.get(tag);
      if (!byCity) {
        byCity = new Map();
        tagCityVenues.set(tag, byCity);
      }
      let set = byCity.get(cid);
      if (!set) {
        set = new Set();
        byCity.set(cid, set);
      }
      set.add(vid);
    }
  }

  const catCityVenues = new Map<string, Map<string, Set<string>>>();
  for (const v of floored) {
    const cat = v.fsq_category;
    if (!cat || !v.city_id) continue;
    let byCity = catCityVenues.get(cat);
    if (!byCity) {
      byCity = new Map();
      catCityVenues.set(cat, byCity);
    }
    let set = byCity.get(v.city_id);
    if (!set) {
      set = new Set();
      byCity.set(v.city_id, set);
    }
    set.add(v.id);
  }

  const reach = cities.slice(0, REACH_CITIES);

  const candidates: Candidate[] = [];
  const rejected: { id: string; label: string; cityCount: number }[] = [];

  const emit = (
    kind: 'tag' | 'category',
    param: string,
    byCity: Map<string, Set<string>>,
    predicate: string,
  ) => {
    const id = keyOf(kind, kind === 'tag' ? param.toLowerCase() : param);
    const cityCount = city ? (byCity.get(city.id)?.size ?? 0) : 0;
    const perCity = reach
      .map((c) => ({ city: c.name, n: byCity.get(c.id)?.size ?? 0 }))
      .filter((r) => r.n > 0)
      .sort((a, b) => b.n - a.n || a.city.localeCompare(b.city));
    const citiesClearing = perCity.filter((r) => r.n >= MIN_VENUES).length;

    /* The bar is the SELECTED city when one is chosen — a rail that fills in New York and not
       here is not a proposal for here. With no city, fall back to reach. */
    const passes = city ? cityCount >= MIN_VENUES : citiesClearing > 0;
    if (!passes) {
      rejected.push({ id, label: kind === 'tag' ? `#${param}` : param, cityCount });
      return;
    }
    const hit = existing.get(id);
    candidates.push({
      id,
      kind,
      tag: kind === 'tag' ? param.toLowerCase() : null,
      categories: kind === 'category' ? [param] : null,
      predicate,
      cityCount,
      citiesClearing,
      byCity: perCity,
      alreadyExists: Boolean(hit),
      existingTitle: hit?.title ?? null,
      existingActive: Boolean(hit?.is_active),
    });
  };

  tagCityVenues.forEach((byCity, tag) => {
    emit(
      'tag',
      tag,
      byCity,
      `kind='tag', tag='${tag}' — venues in the reader's city with a live log carrying the thought tag "${tag}", ${RATER_FLOOR}+ distinct raters`,
    );
  });
  catCityVenues.forEach((byCity, cat) => {
    emit(
      'category',
      cat,
      byCity,
      `kind='category', categories=['${cat}'] — venues in the reader's city whose fsq_category is "${cat}", ${RATER_FLOOR}+ distinct raters`,
    );
  });

  /* Criterion is a closed set of five, so it is listed rather than enumerated. Its membership arm
     compares a venue's shrunk per-criterion score against the product-wide prior, which this file
     deliberately does not replicate — an unknown count is shown as such, never as a zero. */
  for (const c of CRITERIA) {
    const id = keyOf('criterion', c);
    const hit = existing.get(id);
    if (hit) continue; /* four of the five are already live rails */
    candidates.push({
      id,
      kind: 'criterion',
      tag: c,
      categories: null,
      predicate: `kind='criterion', tag='${c}' — venues in the reader's city scoring above the product-wide ${c} prior`,
      cityCount: -1 /* not measured here; see the comment above */,
      citiesClearing: 0,
      byCity: [],
      alreadyExists: false,
      existingTitle: null,
      existingActive: false,
    });
  }

  candidates.sort((a, b) => b.cityCount - a.cityCount || a.id.localeCompare(b.id));
  rejected.sort((a, b) => b.cityCount - a.cityCount);

  const cityPoolRows = city ? pool.filter((v) => v.city_id === city.id) : [];
  return {
    city,
    cityPool: cityPoolRows.length,
    cityPoolFloored: cityPoolRows.filter((v) => (v.venue_shado_unique_users ?? 0) >= RATER_FLOOR).length,
    candidates,
    rejected: rejected.slice(0, 40),
    liveRails,
    measuredCities: reach.map((c) => c.name),
  };
}

/**
 * The authoritative count for a guide that now EXISTS, straight from production's own function.
 *
 * `admin_list_search_guides(p_city_id)` computes `venue_count` as
 * `search_guide_venue_ids(g.id, coalesce(g.city_id, p_city_id), 1000)` — the exact call
 * `get_search_guides` makes to fill the rail. There is no second opinion about what a collection
 * contains, which is why the draft is materialised before the number is trusted.
 */
export async function authoritativeCount(
  guideId: string,
  cityId: string | null,
): Promise<{ count: number; renders: boolean } | null> {
  const { data, error } = await supabase.rpc('admin_list_search_guides', { p_city_id: cityId });
  if (error || !Array.isArray(data)) return null;
  const row = (data as { id?: string; venue_count?: number; renders?: boolean }[]).find((g) => g.id === guideId);
  if (!row) return null;
  return { count: Number(row.venue_count ?? 0) || 0, renders: Boolean(row.renders) };
}
